import { useState, useCallback } from "react"
import { AxiosResponse } from "axios"
import { useEngine } from "hooks/api/useEngine"
import { useAsync } from "hooks/core/useAsync"
import { QueryInstance } from "types/engine.types"
import { EngineQueryResult, EngineOutputItem } from "types/engineOutput.types"

/**
 * Result interface for async operations
 */
export interface AsyncOperationResult {
  success: boolean
  data?: EngineOutputItem | EngineOutputItem[]
  error?: string
}

/**
 * Result interface for script execution
 */
export interface ScriptExecutionResult {
  success: boolean
  data?: EngineOutputItem | EngineOutputItem[]
  error?: string
}

/**
 * Polling result response structure
 */
interface PollingResult {
  status: number
}

/**
 * Return interface for useReportingDataLoading hook
 */
export interface UseReportingDataLoadingReturn {
  /** Execute a script using the engine and handle both sync and async results */
  executeScript: (
    script: string,
    elementId?: string
  ) => Promise<ScriptExecutionResult>
  /** Whether a script is currently executing */
  isLoading: boolean
  /** Current error state */
  error: string | null
  /** Clear any current error state */
  clearError: () => void
}

/**
 * Custom hook for handling reporting data loading with engine queries and async polling
 *
 * @param queryInstance - The engine instance ID
 * @param isTimeSeries - Whether this is for time series data (returns array vs single object)
 * @returns Hook interface with executeScript function and loading states
 *
 * @example
 * const { executeScript, isLoading, error, clearError } = useReportingDataLoading(queryInstance, false)
 *
 * // Execute a script
 * const result = await executeScript('XTAB table1 <<var1>> <<var2>>', 'chart-1')
 * if (result.success) {
 *   console.log('Data:', result.data)
 * } else {
 *   console.error('Error:', result.error)
 * }
 */
const useReportingDataLoading = (
  queryInstance: QueryInstance | null,
  isTimeSeries: boolean = false
): UseReportingDataLoadingReturn => {
  const { RunEngineQuery } = useEngine()
  const { pollingAsync, getAsyncResult } = useAsync()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Polls for asynchronous engine query results
   * @param asyncID - The async operation ID from the initial engine response
   * @returns Result object or null if still polling
   */
  const pollForAsyncResult = useCallback(
    async (asyncID: string): Promise<AsyncOperationResult | null> => {
      try {
        const pollResult: PollingResult = await pollingAsync(asyncID)
        if (pollResult.status === 200) {
          const asyncResult: AxiosResponse<unknown> = await getAsyncResult(
            asyncID
          )
          const engineResult = asyncResult.data as EngineQueryResult

          if (isTimeSeries) {
            // Time series expects array of results
            const resultArray = engineResult?.result?.output
            if (Array.isArray(resultArray) && resultArray.length > 0) {
              return { success: true, data: resultArray }
            } else {
              return {
                success: false,
                error: "Invalid async result format (expected array of tables)",
              }
            }
          } else {
            // Other charts expect single result
            const resultData = engineResult?.result?.output?.[0]
            if (resultData) {
              return { success: true, data: resultData }
            } else {
              return { success: false, error: "Invalid async result format" }
            }
          }
        } else if (pollResult.status === 202) {
          // Continue polling after delay
          setTimeout(() => pollForAsyncResult(asyncID), 5000)
          return null // Still polling
        } else {
          return {
            success: false,
            error: `Polling failed with status ${pollResult.status}`,
          }
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : "Unknown error"
        return {
          success: false,
          error: `Polling error: ${errorMessage}`,
        }
      }
    },
    [pollingAsync, getAsyncResult, isTimeSeries]
  )

  /**
   * Executes a script using the engine and handles both sync and async results
   * @param script - The script to execute
   * @param elementId - Element ID for logging purposes
   * @returns Result object with success/error and data
   */
  const executeScript = useCallback(
    async (
      script: string,
      elementId: string = "unknown"
    ): Promise<ScriptExecutionResult> => {
      if (!queryInstance) {
        const errorMsg = "No query instance"
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }
      if (!script) {
        const errorMsg = "No script provided"
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }

      setIsLoading(true)
      setError(null)

      try {
        const result: AxiosResponse<unknown> = await RunEngineQuery(
          queryInstance,
          script,
          true
        )

        if (result.status === 202) {
          // Async operation - start polling
          const asyncID = result.data as string
          const pollResult = await pollForAsyncResult(asyncID)
          setIsLoading(false)

          if (pollResult) {
            if (pollResult.success) {
              setError(null)
              return { success: true, data: pollResult.data }
            } else {
              console.error(
                `Async operation failed for ${elementId}:`,
                pollResult.error
              )
              setError(pollResult.error || "Async operation failed")
              return { success: false, error: pollResult.error }
            }
          }
          // Still polling - this shouldn't happen with current implementation
          return { success: false, error: "Polling operation incomplete" }
        } else if (result.status === 200) {
          // Sync result
          setIsLoading(false)

          const engineResult = result.data as EngineQueryResult

          if (isTimeSeries) {
            const resultArray = engineResult?.result?.output
            if (Array.isArray(resultArray) && resultArray.length > 0) {
              setError(null)
              return { success: true, data: resultArray }
            } else {
              const errorMsg =
                "Unexpected response format (expected array of tables in output)"
              console.error(`${errorMsg} for ${elementId}`, result)
              setError(errorMsg)
              return { success: false, error: errorMsg }
            }
          } else {
            const resultData = engineResult?.result?.output?.[0]
            if (resultData) {
              setError(null)
              return { success: true, data: resultData }
            } else {
              const errorMsg = "Invalid response format from server"
              console.error(`${errorMsg} for ${elementId}`, result)
              setError(errorMsg)
              return { success: false, error: errorMsg }
            }
          }
        } else {
          setIsLoading(false)
          const errorMsg = `Unexpected response status: ${result.status}`
          console.error(`${errorMsg} for ${elementId}`, result)
          setError(errorMsg)
          return { success: false, error: errorMsg }
        }
      } catch (e) {
        setIsLoading(false)
        const errorMsg =
          e instanceof Error
            ? `Script error: ${e.message}`
            : "Script error: Unknown error"
        console.error(`Script execution error for ${elementId}:`, e)
        setError(errorMsg)
        return { success: false, error: errorMsg }
      }
    },
    [queryInstance, RunEngineQuery, pollForAsyncResult]
  )

  /**
   * Clears any current error state
   */
  const clearError = useCallback((): void => {
    setError(null)
  }, [])

  return {
    executeScript,
    isLoading,
    error,
    clearError,
  }
}

export default useReportingDataLoading
